<template>
  <div>
    <template v-for="(field, i) in fields" :key="i">
      <template v-if="field.type === 'checkbox_group'">
        <div :class="formItemClasses" class="space-y-2">
          <label
            :class="labelClasses"
            class="mt-5 w-[200px] pr-4 text-right text-sm font-medium leading-none opacity-80 peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            {{ field.label }}
          </label>
          <div
            class="w-full"
            :class="{
            'space-y-2 pt-2': field.fields.length > 1,
          }"
          >
            <template v-for="subfield in field.fields" :key="subfield.name">
              <UiFormField
                v-slot="{ value, handleChange }"
                :name="subfield.name"
              >
                <UiFormItem
                  class="flex flex-row items-start space-x-3 space-y-0"
                >
                  <UiFormControl>
                    <UiCheckbox
                      :id="subfield.name"
                      :checked="value"
                      @update:checked="handleChange"
                    />
                  </UiFormControl>
                  <UiLabel class="font-normal">{{ subfield.label }}</UiLabel>
                  <UiFormMessage />
                </UiFormItem>
              </UiFormField>
            </template>
          </div>
        </div>
      </template>

      <template v-else>
        <UiFormField
          v-slot="{ value, handleChange, componentField }"
          :name="field.name"
        >
          <UiFormItem :class="formItemClasses">
            <UiFormLabel :class="labelClasses">{{ field.label }}</UiFormLabel>
            <div class="w-full">
              <UiFormControl>
                <template v-if="field.type === 'file'">
                  <YouTubeThumbnailInput
                    class="max-h-[120px] max-w-[400px]"
                    :allow-types="field.allowedTypes"
                    :max-size="field.maxSize"
                    :model-value="value"
                    @update:model-value="handleChange"
                  />
                </template>
                <template v-else-if="field.type === 'text'">
                  <Input
                    spellcheck="false"
                    type="text"
                    :placeholder="field.placeholder"
                    v-bind="componentField"
                  />
                </template>
                <template v-else-if="field.type === 'textarea'">
                  <Textarea
                    v-bind="componentField"
                    :placeholder="field.placeholder"
                  />
                </template>
                <template v-else-if="field.type === 'checkbox'">
                  <UiCheckbox
                    :id="field.name"
                    :class="{ 'mt-[9px]': field.label }"
                    :checked="value"
                    @update:checked="handleChange"
                  />
                </template>
                <template v-else-if="field.type === 'select'">
                  <UiSelect v-bind="componentField">
                    <UiSelectTrigger>
                      <UiSelectValue :placeholder="field.placeholder" />
                    </UiSelectTrigger>
                    <UiSelectContent>
                      <UiSelectGroup>
                        <UiSelectItem
                          v-for="option in isRef(field.options)
                          ? unref(field.options)
                          : field.options"
                          :key="option.value"
                          :value="option.value"
                        >
                          {{ option.label }}
                        </UiSelectItem>
                      </UiSelectGroup>
                    </UiSelectContent>
                  </UiSelect>
                </template>
                <template v-else-if="field.type === 'combo'">
                  <ComboInput
                    :placeholder="field.placeholder"
                    :options="unref(field.options)"
                    :model-value="value"
                    @update:model-value="handleChange"
                  />
                </template>
                <template v-else-if="field.type === 'twitch_tags'">
                  <UiTagsInput
                    :model-value="value"
                    @update:model-value="handleChange"
                  >
                    <UiTagsInputItem
                      v-for="item in value || []"
                      :key="item"
                      :value="item"
                      :class="{
                      'bg-red-600/40 text-red-200':
                        !twitchTagSchema.safeParse(item).success,
                    }"
                    >
                      <UiTagsInputItemText />
                      <UiTagsInputItemDelete />
                    </UiTagsInputItem>
                    <UiTagsInputInput
                      :placeholder="value?.length
                      ? 'Tag...'
                      : `Hit 'Enter' after each tag...`
                      "
                    />
                  </UiTagsInput>
                </template>
                <template v-else-if="field.type == 'combo_box_tags'">
                  <UiComboBoxTags
                    :model-value="value"
                    @update:model-value="handleChange"
                    :options="unref(field.options)"
                    :placeholder="field.placeholder"
                  />
                </template>
                <template v-else-if="field.type === 'twitch_category'">
                  <TwitchCategoryCombo
                    :model-value="value"
                    @update:model-value="handleChange"
                  />
                </template>
                <template v-else-if="field.type === 'tiktok_category'">
                  <TwitchCategoryCombo
                    :model-value="value"
                    @update:model-value="handleChange"
                    query-path="/api/v5/obs-plugin/go-live/tiktok/info"
                    :default-categories="[]"
                    :map-item="(item) => ({
                      name: item.full_name as string,
                      id: item.game_mask_id as string,
                    })
                      "
                  />
                </template>
                <template v-else-if="field.type === 'kick_category'">
                  <TwitchCategoryCombo
                    :model-value="value"
                    @update:model-value="handleChange"
                    query-path="/api/v5/obs-plugin/go-live/kick/info"
                    :default-categories="[]"
                    :map-item="(item) => ({
                      thumbnail: item.thumbnail as string,
                      name: item.name as string,
                      id: `${item.id}`,
                    })
                      "
                  />
                </template>
                <template v-else-if="field.type === 'radio_group'">
                  <UiRadioGroup
                    :value="value"
                    @update:value="handleChange"
                    :class="{ 'flex flex-column items-center h-full': field.orientation === 'horizontal' }"
                    :default-value="field.defaultValue"
                  >
                    <template
                      v-for="option in field.options"
                      :key="option.value"
                    >
                      <div
                        class="flex items-center gap-3 space-y-0"
                        :class="{
                        'mb-2': field.orientation !== 'horizontal',
                        'mt-3': field.orientation === 'horizontal'
                      }"
                      >
                        <UiRadioGroupItem
                          :value="option.value"
                          :id="option.value"
                        />
                        <UiLabel class="font-normal" :for="option.value"
                          >{{ option.label }}</UiLabel
                        >
                      </div>
                    </template>
                  </UiRadioGroup>
                </template>
              </UiFormControl>
              <template v-if="field.type === 'checkbox' && field.field_label">
                <UiFormLabel class="ml-3 font-normal"
                  >{{ field.field_label }}</UiFormLabel
                >
              </template>
              <UiFormMessage />
            </div>
          </UiFormItem>
        </UiFormField>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
const formItemClasses = computed(() => 'flex');
const labelClasses = computed(() => [
  'text-right',
  'pr-4',
  'mt-5',
  'w-[200px]',
  'opacity-80',
  'font-normal',
  'leading-snug',
]);

defineProps<{ fields: any[] }>();
</script>
